import type { ReactNode } from 'react';
import { Zap, UserCheck } from 'lucide-react';
import { createElement } from 'react';
import { formatPhone } from './formatPhone';
import type { ListEntityKind, FilterSpec } from '../types/list';
import { CHANNEL_LABELS, PERSON_SOURCE_CHANNELS } from '../types/list';
import { US_STATES, NCES_LEVELS, CUSTOMER_STATUS_OPTIONS, STAGE_OPTIONS, TEMPERATURE_OPTIONS } from './listFacets';
import { ORG_TYPE_SELECT_OPTIONS, orgTypeLabel } from './orgTypes';
import { KNOWN_LEVELS } from './levelRank';
import { CustomerStatusBadge } from '../components/organizations/CustomerStatusBadge';
import { TemperatureIcon } from '../components/shared/temperatureConfig';
import { TagChips } from '../components/tags';
import type { CustomerStatus, Temperature } from '../types';

export type FilterControl =
  | { type: 'text'; axis: keyof FilterSpec }
  | { type: 'enum'; axis: keyof FilterSpec; options: readonly { value: string; label: string }[] }
  | { type: 'boolean'; axis: keyof FilterSpec }          // any / yes / no
  | { type: 'range'; minAxis: keyof FilterSpec; maxAxis: keyof FilterSpec }
  | { type: 'dateRange'; fromAxis: keyof FilterSpec; toAxis: keyof FilterSpec }
  | { type: 'tags' };                                    // hardwired to tagIds / tagMode

export interface ExploreColumn {
  key: string;                 // stable id (also localStorage key part)
  label: string;
  sortField?: string;          // present => sortable (must match backend SORTABLE)
  filter?: FilterControl;      // present => filterable
  defaultVisible: boolean;
  render: (row: any) => unknown; // returns display value; the grid wraps it
  cell?: (row: any) => ReactNode; // optional styled renderer — used in table cells + phone cards
}

const ORG_TYPE_OPTIONS = ORG_TYPE_SELECT_OPTIONS;
const CHANNEL_OPTIONS = PERSON_SOURCE_CHANNELS.map((v) => ({ value: v, label: CHANNEL_LABELS[v] ?? v }));
const GENDER_OPTIONS = [{ value: 'BOYS', label: 'Boys' }, { value: 'GIRLS', label: 'Girls' }, { value: 'COED', label: 'Coed' }];
const SEASON_OPTIONS = ['FALL', 'WINTER', 'SPRING', 'SUMMER'].map((v) => ({ value: v, label: v[0] + v.slice(1).toLowerCase() }));
const LEVEL_OPTIONS = KNOWN_LEVELS.map((v) => ({ value: v, label: v }));

// ── Shared cell helpers ──────────────────────────────────────────────────────

/** Subtle pill for enum values (Type, Stage, etc.) */
function PillCell({ label }: { label: string }): ReactNode {
  return createElement(
    'span',
    { className: 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
    label,
  );
}

const STAGE_COLORS: Record<string, string> = {
  LEAD:     'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
  PROSPECT: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  CUSTOMER: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  CHURNED:  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

const STAGE_LABELS: Record<string, string> = {
  LEAD:     'Lead',
  PROSPECT: 'Prospect',
  CUSTOMER: 'Customer',
  CHURNED:  'Churned',
};

function StagePill({ stage }: { stage: string | null | undefined }): ReactNode {
  if (!stage) return createElement('span', { className: 'text-gray-400 dark:text-gray-600' }, '—');
  const colorClass = STAGE_COLORS[stage] ?? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  const label = STAGE_LABELS[stage] ?? stage;
  return createElement(
    'span',
    { className: `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}` },
    label,
  );
}

function AssignedCell({ name }: { name: string | null | undefined }): ReactNode {
  if (!name) {
    return createElement(
      'span',
      { className: 'text-xs text-gray-400 dark:text-gray-600' },
      'Unassigned',
    );
  }
  return createElement(
    'span',
    { className: 'inline-flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300' },
    createElement(UserCheck, { className: 'h-3.5 w-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0' }),
    name,
  );
}

function InBlastCell({ linked }: { linked: boolean }): ReactNode {
  if (!linked) {
    return createElement('span', { className: 'text-gray-400 dark:text-gray-600' }, '—');
  }
  return createElement(
    'span',
    {
      className:
        'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      title: 'Linked to Blast',
    },
    createElement(Zap, { className: 'h-3 w-3' }),
    'Blast',
  );
}

function TemperatureCell({ temperature }: { temperature: Temperature | null | undefined }): ReactNode {
  if (!temperature) return createElement('span', { className: 'text-gray-400 dark:text-gray-600' }, '—');
  const labelMap: Record<Temperature, string> = { HOT: 'Hot', WARM: 'Warm', COLD: 'Cold', FREEZING: 'Freezing' };
  return createElement(
    'span',
    { className: 'inline-flex items-center gap-1' },
    createElement(TemperatureIcon, { temperature, className: 'h-4 w-4' }),
    createElement('span', { className: 'text-sm text-gray-700 dark:text-gray-300' }, labelMap[temperature]),
  );
}

// ── Column definitions ───────────────────────────────────────────────────────

const ORG_COLUMNS: ExploreColumn[] = [
  { key: 'name', label: 'Name', sortField: 'name', filter: { type: 'text', axis: 'search' }, defaultVisible: true, render: (r) => r.organization.name },
  {
    key: 'type', label: 'Type', filter: { type: 'enum', axis: 'orgTypes', options: ORG_TYPE_OPTIONS }, defaultVisible: true,
    render: (r) => r.organization.type,
    cell: (r) => {
      const label = orgTypeLabel(r.organization.type);
      return label ? PillCell({ label }) : createElement('span', { className: 'text-gray-400' }, '—');
    },
  },
  { key: 'location', label: 'City, ST', filter: { type: 'enum', axis: 'states', options: US_STATES }, defaultVisible: true, render: (r) => [r.organization.city, r.organization.state].filter(Boolean).join(', ') },
  {
    key: 'assigned', label: 'Assigned', filter: { type: 'boolean', axis: 'assigned' }, defaultVisible: true,
    render: (r) => r.organization.assignedRep?.displayName ?? null,
    cell: (r) => AssignedCell({ name: r.organization.assignedRep?.displayName }),
  },
  { key: 'coaches', label: 'Coaches', defaultVisible: true, render: (r) => r.organization.coachCount ?? 0 },
  {
    key: 'customerStatus', label: 'Customer status', filter: { type: 'enum', axis: 'customerStatus', options: CUSTOMER_STATUS_OPTIONS }, defaultVisible: true,
    render: (r) => r.organization.customerStatus,
    cell: (r) => {
      const s: CustomerStatus | null = r.organization.customerStatus;
      return s ? createElement(CustomerStatusBadge, { status: s, size: 'sm' }) : createElement('span', { className: 'text-gray-400' }, '—');
    },
  },
  {
    key: 'tags', label: 'Tags', filter: { type: 'tags' }, defaultVisible: true,
    render: (r) => (r.organization.tags ?? []).map((t: any) => t.code).join(' '),
    cell: (r) => createElement(TagChips, { tags: r.organization.tags ?? [], max: 3 }),
  },
  { key: 'lastActivity', label: 'Last activity', sortField: 'lastActivityAt', filter: { type: 'dateRange', fromAxis: 'lastActivityFrom', toAxis: 'lastActivityTo' }, defaultVisible: true, render: (r) => r.organization.lastActivityAt },
  {
    key: 'inBlast', label: 'In Blast', filter: { type: 'boolean', axis: 'hasBlastLink' }, defaultVisible: true,
    render: (r) => !!r.organization.blastSchoolId,
    cell: (r) => InBlastCell({ linked: !!r.organization.blastSchoolId }),
  },
  // optional
  { key: 'level', label: 'Level', filter: { type: 'enum', axis: 'ncesLevels', options: NCES_LEVELS }, defaultVisible: false, render: (r) => r.organization.ncesLevel },
  // Filter is the point (org has sport X); render is the active-sport count.
  // ExploreGrid + FilterSheet inject the dynamic Blast sport options by matching axis === 'sports'.
  { key: 'sports', label: 'Sports', filter: { type: 'enum', axis: 'sports', options: [] }, defaultVisible: false, render: (r) => r.organization.sportCount ?? 0 },
  { key: 'enrollment', label: 'Enrollment', sortField: 'ncesEnrollment', filter: { type: 'range', minAxis: 'enrollmentMin', maxAxis: 'enrollmentMax' }, defaultVisible: false, render: (r) => r.organization.ncesEnrollment },
  { key: 'private', label: 'Private', filter: { type: 'boolean', axis: 'isPrivate' }, defaultVisible: false, render: (r) => r.organization.isPrivate },
  { key: 'lastVisit', label: 'Last visit', sortField: 'lastVisitAt', filter: { type: 'dateRange', fromAxis: 'lastVisitFrom', toAxis: 'lastVisitTo' }, defaultVisible: false, render: (r) => r.organization.lastVisitAt },
  { key: 'district', label: 'District', defaultVisible: false, render: (r) => r.organization.district },
];

const PERSON_COLUMNS: ExploreColumn[] = [
  { key: 'name', label: 'Name', sortField: 'lastName', filter: { type: 'text', axis: 'search' }, defaultVisible: true, render: (r) => `${r.person.firstName} ${r.person.lastName}` },
  { key: 'position', label: 'Position', filter: { type: 'text', axis: 'role' }, defaultVisible: true, render: (r) => r.person.position ?? r.person.organizations?.[0]?.role },
  { key: 'sport', label: 'Sport', filter: { type: 'enum', axis: 'sports', options: [] }, defaultVisible: true, render: (r) => r.person.organizations?.[0]?.sport?.name },
  { key: 'school', label: 'School', defaultVisible: true, render: (r) => r.person.organizations?.[0]?.organization?.name },
  { key: 'location', label: 'City, ST', filter: { type: 'enum', axis: 'states', options: US_STATES }, defaultVisible: true, render: (r) => r.person.organizations?.[0]?.organization?.state },
  {
    key: 'stage', label: 'Stage', filter: { type: 'enum', axis: 'stages', options: STAGE_OPTIONS }, defaultVisible: true,
    render: (r) => r.person.stage,
    cell: (r) => StagePill({ stage: r.person.stage }),
  },
  {
    key: 'temperature', label: 'Temperature', filter: { type: 'enum', axis: 'temperatures', options: TEMPERATURE_OPTIONS }, defaultVisible: true,
    render: (r) => r.person.temperature,
    cell: (r) => TemperatureCell({ temperature: r.person.temperature }),
  },
  {
    key: 'tags', label: 'Tags', filter: { type: 'tags' }, defaultVisible: true,
    render: (r) => (r.person.tags ?? []).map((t: any) => t.code).join(' '),
    cell: (r) => createElement(TagChips, { tags: r.person.tags ?? [], max: 3 }),
  },
  {
    key: 'assigned', label: 'Assigned', filter: { type: 'boolean', axis: 'assigned' }, defaultVisible: true,
    render: (r) => r.person.assignedUser?.displayName ?? null,
    cell: (r) => AssignedCell({ name: r.person.assignedUser?.displayName }),
  },
  { key: 'lastActivity', label: 'Last activity', sortField: 'lastActivityAt', filter: { type: 'dateRange', fromAxis: 'lastActivityFrom', toAxis: 'lastActivityTo' }, defaultVisible: true, render: (r) => r.person.lastActivityAt },
  {
    key: 'inBlast', label: 'In Blast', filter: { type: 'boolean', axis: 'inBlast' }, defaultVisible: true,
    render: (r) => !!r.person.blastUserId,
    cell: (r) => InBlastCell({ linked: !!r.person.blastUserId }),
  },
  // optional
  { key: 'dnc', label: 'Do-Not-Call', filter: { type: 'boolean', axis: 'excludeDnc' }, defaultVisible: false, render: (r) => r.person.doNotCall },
  { key: 'email', label: 'Email', defaultVisible: false, render: (r) => r.person.email },
  { key: 'phone', label: 'Phone', defaultVisible: false, render: (r) => formatPhone(r.person.phone) },
  {
    key: 'source', label: 'Origin', filter: { type: 'enum', axis: 'channels', options: CHANNEL_OPTIONS }, defaultVisible: false,
    render: (r) => CHANNEL_LABELS[r.person.source] ?? r.person.source ?? '—',
    cell: (r) => {
      const label = CHANNEL_LABELS[r.person.source] ?? r.person.source;
      return label
        ? PillCell({ label })
        : createElement('span', { className: 'text-gray-400 dark:text-gray-600' }, '—');
    },
  },
];

const PROGRAM_COLUMNS: ExploreColumn[] = [
  { key: 'sport', label: 'Sport', filter: { type: 'enum', axis: 'sports', options: [] }, defaultVisible: true, render: (r) => r.team.sport?.name },
  { key: 'level', label: 'Level', sortField: 'level', filter: { type: 'enum', axis: 'levels', options: LEVEL_OPTIONS }, defaultVisible: true, render: (r) => r.team.level },
  { key: 'school', label: 'School', defaultVisible: true, render: (r) => r.team.sport?.organization?.name },
  { key: 'location', label: 'City, ST', filter: { type: 'enum', axis: 'states', options: US_STATES }, defaultVisible: true, render: (r) => r.team.sport?.organization?.state },
  { key: 'roster', label: 'Roster', sortField: 'rosterCount', filter: { type: 'range', minAxis: 'rosterMin', maxAxis: 'rosterMax' }, defaultVisible: true, render: (r) => r.team.rosterCount },
  {
    key: 'assigned', label: 'Assigned', filter: { type: 'boolean', axis: 'assigned' }, defaultVisible: true,
    render: (r) => r.team.sport?.organization?.assignedRep?.displayName ?? null,
    cell: (r) => AssignedCell({ name: r.team.sport?.organization?.assignedRep?.displayName }),
  },
  {
    key: 'customerStatus', label: 'Customer status', filter: { type: 'enum', axis: 'customerStatus', options: CUSTOMER_STATUS_OPTIONS }, defaultVisible: true,
    render: (r) => r.team.sport?.organization?.customerStatus,
    cell: (r) => {
      const s: CustomerStatus | null = r.team.sport?.organization?.customerStatus;
      return s ? createElement(CustomerStatusBadge, { status: s, size: 'sm' }) : createElement('span', { className: 'text-gray-400' }, '—');
    },
  },
  // optional
  { key: 'gender', label: 'Gender', filter: { type: 'enum', axis: 'genders', options: GENDER_OPTIONS }, defaultVisible: false, render: (r) => r.team.sport?.gender },
  { key: 'hasActiveStore', label: 'Store', filter: { type: 'boolean', axis: 'hasActiveStore' }, defaultVisible: false, render: (r) => (r.team.sport?.hasActiveStore ? 'Yes' : '—') },
  { key: 'season', label: 'Season', filter: { type: 'enum', axis: 'seasons', options: SEASON_OPTIONS }, defaultVisible: false, render: (r) => r.team.sport?.season },
];

const REGISTRY: Record<string, ExploreColumn[]> = { ORG: ORG_COLUMNS, PERSON: PERSON_COLUMNS, PROGRAM: PROGRAM_COLUMNS };

export const EXPLORE_TABS: { key: ListEntityKind; label: string }[] = [
  { key: 'ORG', label: 'Orgs' },
  { key: 'PROGRAM', label: 'Programs' },
  { key: 'PERSON', label: 'People' },
];

export function columnsFor(kind: ListEntityKind): ExploreColumn[] { return REGISTRY[kind] ?? []; }
export function defaultVisible(kind: ListEntityKind): string[] {
  return columnsFor(kind).filter((c) => c.defaultVisible).map((c) => c.key);
}
